import type { SecretEnvelope } from './loopService';

const base64 = (buffer: ArrayBuffer): string => btoa(
  Array.from(new Uint8Array(buffer), (byte) => String.fromCharCode(byte)).join(''),
);

const importPublicKey = async (key: string | globalThis.JsonWebKey): Promise<globalThis.CryptoKey> => {
  const algorithm = { name: 'RSA-OAEP', hash: 'SHA-256' };

  if (typeof key !== 'string') return globalThis.crypto.subtle.importKey('jwk', key, algorithm, false, ['encrypt']);
  if (key.trim().startsWith('{')) return importPublicKey(JSON.parse(key) as globalThis.JsonWebKey);
  const encoded = key.replace(/-----[^-]+-----/g, '').replace(/\s/g, '');
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));

  return globalThis.crypto.subtle.importKey('spki', bytes, algorithm, false, ['encrypt']);
};

/** Only the selected extension profile holds the private key. No plaintext leaves this function. */
// eslint-disable-next-line max-statements
export const encryptLoopSecret = async (
  values: Record<string, string>, publicKey: string | globalThis.JsonWebKey,
  binding: { profile_id: string; run_id: string; question_id: string; domain: string },
): Promise<SecretEnvelope> => {
  if (!globalThis.crypto?.subtle) throw new Error('Secure input requires HTTPS or localhost.');
  const aad = JSON.stringify(binding);
  const recipient = await importPublicKey(publicKey);
  const contentKey = await globalThis.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await globalThis.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: new globalThis.TextEncoder().encode(aad) }, contentKey,
    new globalThis.TextEncoder().encode(JSON.stringify({ values })),
  );
  const rawKey = await globalThis.crypto.subtle.exportKey('raw', contentKey);
  const wrapped = await globalThis.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, recipient, rawKey);

  return { algorithm: 'RSA-OAEP-256+A256GCM', encrypted_key: base64(wrapped), iv: base64(iv.buffer),
    ciphertext: base64(ciphertext), aad };
};

export const maySaveLoopSecret = (kind: string): boolean => (
  !/payment|credit.?card|card.?number|cvv|cvc|otp|one.?time|captcha|security.?code|verification.?code/i.test(kind)
);

/** Match the fingerprint shown by the paired extension, independent of JWK property order. */
export const loopKeyFingerprint = async (key: string | globalThis.JsonWebKey): Promise<string> => {
  const jwk = typeof key === 'string' ? JSON.parse(key) as globalThis.JsonWebKey : key;
  const canonical = JSON.stringify({ kty: jwk.kty, e: jwk.e, n: jwk.n });
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new globalThis.TextEncoder().encode(canonical));
  const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase();

  return hex.match(/.{4}/g)?.join(' ') || hex;
};
